/**
 * The generated interface of a chat line (« interface générative dans le fil », docs/design/APP-APPLE.md §6.7): a
 * json-render catalog (vercel-labs/json-render) shared by the Worker (show_ui validates the model's spec, the main
 * prompt lists the catalog, channels without an interface get its plain-text version) and the web chat (the registry
 * that draws it, components/chat-ui.tsx). Same spec shape as Pulse's summary (./pulse-summary.ts), whose component
 * schemas it reuses where they are identical.
 *
 * `sanitizeChatUiSpec` keeps what the catalog allows and drops the rest element by element, never the whole spec:
 * unknown types, props that fail their schema (lengths included), expressions, visibility / repeat / watch fields,
 * children of the wrong kind (Options → Option, Products → Product, Checklist → CheckItem, DayTimeline → TimelineItem,
 * Highlight → one ActionChip, Row → Stat / PersonChip / Weather / ActionChip), dangling or cyclic children, anything
 * below the 4th level or past the 24th element, and actions other than ask, open (closed page list) and url (https). A
 * pressable element without an action gets its natural one (an Option answers its title, a LinkCard opens its url, a
 * Product with a url opens it); an ActionChip without one is dropped. A Product's stored photo (`photo`, an R2 key set
 * by show_ui) is kept only when the caller asks for it (`photos`), never from the model. The spec is null when nothing
 * visible is left.
 */
import { defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { z } from "zod";
import { CHAT_UI_COMPONENTS, CHAT_UI_ROUTES, type ChatUiAction, type ChatUiComponent, type ChatUiElement, type ChatUiSpec } from "./contracts";
import { ASK_PARAMS, SHARED_UI_PROPS, UI_TONES, uiText as text } from "./pulse-summary";

export const CHAT_UI_MAX_ELEMENTS = 24;
/** Levels of the tree, the root being the first. */
export const CHAT_UI_MAX_DEPTH = 4;
const URL_MAX = 2000;

const tone = z.enum(UI_TONES).nullish();

/** An https address with a host and no credentials in it (LinkCard.url, the url action). */
export function isHttpsUrl(value: string): boolean {
  if (value.length > URL_MAX || /\s/.test(value)) return false;
  try {
    const u = new URL(value);
    return u.protocol === "https:" && u.hostname !== "" && !u.username && !u.password;
  } catch {
    return false;
  }
}

const httpsUrl = z.string().trim().max(URL_MAX).refine(isHttpsUrl, "adresse https attendue");

/**
 * A Product's photo address: only read by show_ui, which downloads it under the public-fetch rules (https and public in
 * production) and stores the bytes; neither the app nor the image card ever loads it.
 */
const imageUrl = z
  .string()
  .trim()
  .max(URL_MAX)
  .refine((v) => /^https?:\/\/[^\s]+$/i.test(v), "adresse https attendue");

/** How a Journey is made. */
export const JOURNEY_MODES = ["train", "plane", "bus", "car", "metro", "tram", "walk", "bike", "boat"] as const;
export type JourneyMode = (typeof JOURNEY_MODES)[number];

/** A stored Product photo (show_ui): the user's own outbound copy, an image type. */
export const CHAT_UI_PHOTO_KEY = /^u\/[A-Za-z0-9_-]{1,64}\/outbound\/[0-9A-HJKMNP-TV-Z]{26}\.(jpg|png|webp|gif)$/;

export const chatUiCatalog = defineCatalog(schema, {
  components: {
    Stack: { props: SHARED_UI_PROPS.Stack, slots: ["default"], description: "Colonne (gap sm ou md). La racine habituelle." },
    Row: {
      props: SHARED_UI_PROPS.Row,
      slots: ["default"],
      description: "Ligne qui passe à la ligne. Enfants : 2 à 4 Stat, PersonChip, Weather ou ActionChip.",
    },
    Headline: { props: SHARED_UI_PROPS.Headline, description: "Un titre d'une phrase (90 caractères max), en tête. Au plus un." },
    Text: { props: z.object({ text: text(280) }), description: "Un paragraphe court (280 caractères max)." },
    DayTimeline: {
      props: SHARED_UI_PROPS.DayTimeline,
      slots: ["default"],
      description: "Frise d'une journée, title facultatif (40). Enfants : TimelineItem uniquement, dans l'ordre.",
    },
    TimelineItem: {
      props: SHARED_UI_PROPS.TimelineItem,
      description: "Un moment : time court (« 10 h », « 14 h 30 » ; 12 max), label (70), note facultative (60), tone accent ou warning pour ce qui presse.",
    },
    Highlight: {
      props: SHARED_UI_PROPS.Highlight,
      slots: ["default"],
      description: "Un point mis en avant : title (70), text facultatif (120), tone. Enfant facultatif : un seul ActionChip.",
    },
    Stat: {
      props: z.object({ value: text(10), label: text(40), tone }),
      description: "Un chiffre réel : value (10 max : « 3 », « 64 € », « 12 km »), label court (40).",
    },
    PersonChip: { props: SHARED_UI_PROPS.PersonChip, description: "Une personne : name (40), et en quelques mots pourquoi elle compte ici, why (60)." },
    Weather: {
      props: SHARED_UI_PROPS.Weather.extend({ day: text(16).nullish(), place: text(30).nullish() }),
      description:
        "La météo, seulement si un outil vient de te la donner : temp (8 : « 14° », « 8–14° »), sky, day facultatif (16 : « Demain », « Sam. »), place facultatif (30), conseil hint facultatif (60). Plusieurs jours : 2 à 4 Weather dans un Row.",
    },
    Progress: { props: SHARED_UI_PROPS.Progress, description: "Une progression réelle : label (60), percent entier de 0 à 100." },
    Options: {
      props: z.object({ layout: z.enum(["list", "grid"]).nullish() }),
      slots: ["default"],
      description: "Des choix à proposer (2 à 6) : list (lignes) ou grid (cartes sur 2 colonnes). Enfants : Option uniquement.",
    },
    Option: {
      props: z.object({ title: text(60), subtitle: text(80).nullish(), meta: text(40).nullish(), badge: text(24).nullish() }),
      description:
        "Un choix : title (60), subtitle (80), meta (40) et badge (24) facultatifs. on.press : ask avec la réponse qu'il envoie à sa place (sans on, son title).",
    },
    LinkCard: {
      props: z.object({ title: text(80), site: text(40), url: httpsUrl }),
      description: "Un lien : title (80), site (40), url https recopiée exactement d'un résultat d'outil ou de la conversation. Le toucher l'ouvre.",
    },
    Sparkline: {
      props: z.object({ label: text(40), values: z.array(z.number()).min(2).max(30), unit: text(6).nullish() }),
      description: "Une petite courbe de valeurs réelles, de la plus ancienne à la plus récente : label (40), values (2 à 30 nombres), unit (6).",
    },
    Checklist: {
      props: z.object({ title: text(40).nullish() }),
      slots: ["default"],
      description: "Une liste à cocher, title facultatif (40). Enfants : CheckItem uniquement.",
    },
    CheckItem: { props: z.object({ label: text(80), done: z.boolean().default(false) }), description: "Un point de la liste : label (80), done (fait ou non)." },
    ActionChip: { props: SHARED_UI_PROPS.ActionChip, description: "Un bouton court (label 40), toujours avec on.press." },
    Table: {
      props: z.object({
        title: text(60).nullish(),
        columns: z.array(text(24)).min(2).max(4),
        rows: z.array(z.array(text(40)).min(2).max(4)).min(1).max(8),
        highlight: z.number().int().min(1).max(8).nullish(),
      }),
      description:
        "Un comparatif en tableau : title facultatif (60), columns (2 à 4 en-têtes de 24 caractères ; la première nomme ce qu'on compare), rows (1 à 8 lignes d'autant de cellules que columns, 40 caractères), highlight facultatif = numéro (1, 2…) de la ligne à mettre en avant.",
    },
    Products: {
      props: z.object({ layout: z.enum(["list", "grid"]).nullish() }),
      slots: ["default"],
      description: "Des produits (1 à 6) : grid (cartes avec photo sur 2 colonnes, par défaut) ou list (lignes). Enfants : Product uniquement.",
    },
    Product: {
      props: z.object({
        title: text(70),
        price: text(16).nullish(),
        subtitle: text(60).nullish(),
        badge: text(24).nullish(),
        image: imageUrl.nullish(),
        url: httpsUrl.nullish(),
      }),
      description:
        "Un produit : title (70), price (16 : « 89,99 € »), subtitle (60 : boutique, couleur, délai), badge (24) ; image = adresse https de sa photo recopiée telle quelle d'un résultat d'outil ; url = sa page https. on.press facultatif : ask pour le choisir (sans on, il ouvre url).",
    },
    Journey: {
      props: z.object({
        mode: z.enum(JOURNEY_MODES),
        from: text(40),
        to: text(40),
        depart: text(12).nullish(),
        arrive: text(12).nullish(),
        duration: text(16).nullish(),
        carrier: text(40).nullish(),
        price: text(16).nullish(),
        note: text(60).nullish(),
        tone,
      }),
      description:
        "Un trajet ou un vol : mode (train, plane, bus, car, metro, tram, walk, bike, boat), from et to (40), depart et arrive (12 : « 8 h 12 »), duration (16 : « 2 h 05 »), carrier (40 : « TGV INOUI 6611 », « AF 1234 »), price (16), note (60 : correspondance, quai, retard), tone warning pour un souci. on.press facultatif : ask pour le choisir.",
    },
  },
  actions: {
    ask: {
      params: ASK_PARAMS,
      description: 'Envoie un message court à sa place, à la première personne, en réponse à ce message. Params : { "message": "…" } (200 caractères max).',
    },
    open: {
      params: z.object({ to: z.enum(CHAT_UI_ROUTES) }),
      description: `Ouvre une page de l'app. Params : { "to": une de ${CHAT_UI_ROUTES.join(", ")} }.`,
    },
    url: {
      params: z.object({ href: httpsUrl }),
      description: 'Ouvre une adresse https dans son navigateur. Params : { "href": "https://…" }.',
    },
  },
});

// The catalog and the contract (CHAT_UI_COMPONENTS) name the same components: otherwise this fails to compile.
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export const CHAT_UI_CATALOG_MATCHES_CONTRACT: Same<keyof typeof chatUiCatalog.data.components, ChatUiComponent> = true;

/** Components that only live inside their own container. */
const INNER: readonly ChatUiComponent[] = ["Option", "CheckItem", "TimelineItem", "Product"];
/** What Stack accepts: every component but the inner ones. */
const BLOCKS = CHAT_UI_COMPONENTS.filter((c) => !INNER.includes(c));
/** The children each container accepts (`max`: how many); every other component is a leaf. */
const CONTAINERS: Partial<Record<ChatUiComponent, { accepts: readonly ChatUiComponent[]; max?: number }>> = {
  Stack: { accepts: BLOCKS },
  Row: { accepts: ["Stat", "PersonChip", "Weather", "ActionChip"] },
  DayTimeline: { accepts: ["TimelineItem"] },
  Highlight: { accepts: ["ActionChip"], max: 1 },
  Options: { accepts: ["Option"] },
  Products: { accepts: ["Product"], max: 6 },
  Checklist: { accepts: ["CheckItem"] },
};
const PRESSABLE: readonly ChatUiComponent[] = ["Option", "ActionChip", "LinkCard", "Product", "Journey"];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function short(s: string, max = 40): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function componentOf(type: unknown): ChatUiComponent | null {
  return typeof type === "string" && (CHAT_UI_COMPONENTS as readonly string[]).includes(type) ? (type as ChatUiComponent) : null;
}

/** The first problem of a failed parse, for the model (« title : Too big… »). */
function issueText(error: z.ZodError): string {
  const issue = error.issues[0];
  if (!issue) return "props invalides";
  return `${issue.path.length ? `${issue.path.join(".")} : ` : ""}${issue.message}`;
}

/** The props as parsed, without the optional ones left empty. */
function compact(props: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(props).filter(([, v]) => v !== null && v !== undefined));
}

/** The press binding as written (undefined when there is none; json-render also allows a list: its first one). */
function pressBinding(on: unknown): unknown {
  if (on === undefined || on === null) return undefined;
  if (!isRecord(on)) return on;
  const press = Array.isArray(on.press) ? on.press[0] : on.press;
  return press ?? undefined;
}

/** A press binding that is exactly one allowed action with valid params, or null. */
export function chatUiAction(binding: unknown): ChatUiAction | null {
  if (!isRecord(binding)) return null;
  const actions = chatUiCatalog.data.actions;
  if (binding.action === "ask") {
    const p = actions.ask.params.safeParse(binding.params);
    return p.success ? { action: "ask", params: p.data } : null;
  }
  if (binding.action === "open") {
    const p = actions.open.params.safeParse(binding.params);
    return p.success ? { action: "open", params: p.data } : null;
  }
  if (binding.action === "url") {
    const p = actions.url.params.safeParse(binding.params);
    return p.success ? { action: "url", params: p.data } : null;
  }
  return null;
}

type Checked =
  | { ok: true; type: ChatUiComponent; props: Record<string, unknown>; children: string[]; press: ChatUiAction | null; photo: string | null }
  | { ok: false; why: string };

/** One element on its own (type, props, action); its children are resolved by the tree walk. */
function checkElement(raw: unknown, photos = false): Checked {
  if (!isRecord(raw)) return { ok: false, why: "pas un élément { type, props, children }" };
  const type = componentOf(raw.type);
  if (!type) return { ok: false, why: typeof raw.type === "string" ? `type inconnu « ${short(raw.type, 30)} »` : "type manquant" };
  const parsed = chatUiCatalog.data.components[type].props.safeParse(raw.props ?? {});
  if (!parsed.success) return { ok: false, why: `${type} : ${issueText(parsed.error)}` };
  const props = compact(parsed.data as Record<string, unknown>);
  if (type === "Table") {
    const width = (props.columns as string[]).length;
    const rows = props.rows as string[][];
    if (rows.some((r) => r.length !== width)) return { ok: false, why: `Table : chaque ligne a ${width} cellules, comme columns` };
    if (typeof props.highlight === "number" && props.highlight > rows.length) delete props.highlight;
  }
  let press: ChatUiAction | null = null;
  if (PRESSABLE.includes(type)) {
    const binding = pressBinding(raw.on);
    if (binding !== undefined) {
      press = chatUiAction(binding);
      if (!press) return { ok: false, why: `${type} : action invalide (ask { message }, open { to } ou url { href } en https)` };
    } else if (type === "Option") {
      press = { action: "ask", params: { message: String(props.title) } };
    } else if (type === "LinkCard") {
      press = { action: "url", params: { href: String(props.url) } };
    } else if (type === "Product") {
      press = typeof props.url === "string" ? { action: "url", params: { href: props.url } } : null;
    } else if (type === "ActionChip") {
      return { ok: false, why: "ActionChip sans action (on.press)" };
    }
  }
  const children = Array.isArray(raw.children) ? raw.children.filter((c): c is string => typeof c === "string") : [];
  const photo = photos && type === "Product" && typeof raw.photo === "string" && CHAT_UI_PHOTO_KEY.test(raw.photo) ? raw.photo : null;
  return { ok: true, type, props, children, press, photo };
}

export interface SanitizedChatUi {
  /** What survives (null: nothing visible left). */
  spec: ChatUiSpec | null;
  /** What was dropped and why, for the model (« « o4 » : Option : title : Too big… »), 12 at most. */
  dropped: string[];
}

/**
 * Keeps what the catalog allows (see the module comment). A root other than a Stack or a Row is wrapped in a Stack.
 * `photos`: keep the Products' stored photos (a spec the server wrote: never the model's).
 */
export function sanitizeChatUiSpec(input: unknown, opts: { photos?: boolean } = {}): SanitizedChatUi {
  const photos = opts.photos === true;
  if (!isRecord(input) || typeof input.root !== "string" || !isRecord(input.elements)) {
    return { spec: null, dropped: ["la spec doit être { root, elements: { <id>: { type, props, children, on? } } }"] };
  }
  const raw = input.elements;
  const out: Record<string, ChatUiElement> = {};
  const seen = new Set<string>();
  const dropped: string[] = [];
  const drop = (id: string, why: string): null => {
    if (dropped.length < 12) dropped.push(`« ${short(id)} » : ${why}`);
    return null;
  };
  let count = 0;

  const visit = (id: string, level: number, accepts: readonly ChatUiComponent[]): string | null => {
    if (!Object.hasOwn(raw, id)) return drop(id, "introuvable dans elements");
    if (seen.has(id)) return drop(id, "déjà placé ailleurs (boucle ou doublon)");
    seen.add(id);
    const el = checkElement(raw[id], photos);
    if (!el.ok) return drop(id, el.why);
    if (!accepts.includes(el.type)) return drop(id, `${el.type} n'a pas sa place ici`);
    if (level > CHAT_UI_MAX_DEPTH) return drop(id, `plus de ${CHAT_UI_MAX_DEPTH} niveaux`);
    if (count >= CHAT_UI_MAX_ELEMENTS) return drop(id, `au-delà de ${CHAT_UI_MAX_ELEMENTS} éléments`);
    count += 1;
    const container = CONTAINERS[el.type];
    const children: string[] = [];
    for (const child of el.children) {
      if (!container) {
        if (!seen.has(child) && Object.hasOwn(raw, child)) {
          seen.add(child);
          drop(child, `un ${el.type} n'a pas d'enfants`);
        }
        continue;
      }
      if (container.max !== undefined && children.length >= container.max) {
        if (!seen.has(child) && Object.hasOwn(raw, child)) {
          seen.add(child);
          drop(child, `un ${el.type} n'a qu'un enfant`);
        }
        continue;
      }
      const kept = visit(child, level + 1, container.accepts);
      if (kept) children.push(kept);
    }
    // A container left empty shows nothing (a Highlight stands on its own).
    if (container && el.type !== "Highlight" && children.length === 0) {
      count -= 1;
      return drop(id, `${el.type} vide`);
    }
    out[id] = { type: el.type, props: el.props, children, ...(el.press ? { on: { press: el.press } } : {}), ...(el.photo ? { photo: el.photo } : {}) };
    return id;
  };

  let root: string | null;
  const first = Object.hasOwn(raw, input.root) ? checkElement(raw[input.root]) : null;
  if (first?.ok && (first.type === "Stack" || first.type === "Row")) {
    root = visit(input.root, 1, ["Stack", "Row"]);
  } else {
    // Any other root: wrapped in a Stack (a key of its own, which counts as one element).
    count = 1;
    const child = visit(input.root, 2, BLOCKS);
    root = null;
    if (child) {
      root = "__root";
      for (let n = 2; Object.hasOwn(raw, root) || Object.hasOwn(out, root); n++) root = `__root${n}`;
      out[root] = { type: "Stack", props: {}, children: [child] };
    }
  }
  for (const id of Object.keys(raw)) if (!seen.has(id)) drop(id, "pas relié à la racine");
  return { spec: root ? { root, elements: out } : null, dropped };
}

/** A stored spec (chat_log.ui) checked again against today's catalog: null when absent, unreadable or empty. */
export function parseChatUi(json: string | null | undefined): ChatUiSpec | null {
  if (!json) return null;
  try {
    return sanitizeChatUiSpec(JSON.parse(json), { photos: true }).spec;
  } catch {
    return null;
  }
}

// ───────────────────────────── Plain-text version ─────────────────────────────

const SKY: Record<"fr" | "en", Record<string, string>> = {
  fr: { sun: "soleil", cloud: "nuageux", rain: "pluie", storm: "orage", snow: "neige", fog: "brouillard", wind: "vent", mixed: "éclaircies" },
  en: { sun: "sunny", cloud: "cloudy", rain: "rain", storm: "storm", snow: "snow", fog: "fog", wind: "windy", mixed: "sunny spells" },
};
const FALLBACK_MAX = 1200;

/** The sign of each way of travelling (plain text and image card). */
export const JOURNEY_ICONS: Record<JourneyMode, string> = {
  train: "🚆",
  plane: "✈️",
  bus: "🚌",
  car: "🚗",
  metro: "🚇",
  tram: "🚊",
  walk: "🚶",
  bike: "🚲",
  boat: "⛴️",
};

/** A Journey on one line: « 🚆 8 h 12 Paris → 10 h 17 Lyon · 2 h 05 · TGV INOUI 6611 · 49 € (quai 7) ». */
export function journeyLine(p: Record<string, unknown>): string {
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  const icon = JOURNEY_ICONS[str(p.mode) as JourneyMode] ?? "";
  const from = [str(p.depart), str(p.from)].filter(Boolean).join(" ");
  const to = [str(p.arrive), str(p.to)].filter(Boolean).join(" ");
  const details = [str(p.duration), str(p.carrier), str(p.price)].filter(Boolean).join(" · ");
  return `${icon ? `${icon} ` : ""}${from} → ${to}${details ? ` · ${details}` : ""}${p.note ? ` (${str(p.note)})` : ""}`;
}

/** A Weather on one line: « Demain à Lyon : 14°, soleil — prends une veste ». */
function weatherLine(p: Record<string, unknown>, fr: boolean): string {
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  const when = [str(p.day), p.place ? `${fr ? "à" : "in"} ${str(p.place)}` : ""].filter(Boolean).join(" ");
  const sky = SKY[fr ? "fr" : "en"][str(p.sky)] ?? str(p.sky);
  return `${when ? `${when}${fr ? " : " : ": "}` : ""}${str(p.temp)}, ${sky}${p.hint ? ` — ${str(p.hint)}` : ""}`;
}

/**
 * The interface as a few plain-text lines, for the channels that show none (iMessage, SMS, Telegram, e-mail) and for
 * the prompts that read the conversation: options as a numbered list (« 1. Pinard — Bistrot · 6 min »), a timeline as
 * « 10 h · … » lines, a link as « title — url », a checklist with ☑ / ☐, figures side by side as « 3 validations · … ».
 * Buttons that only send a message or open a page are left out (a url one gives its address). Never JSON; "" when
 * nothing is left. French, or English for any other language.
 */
export function chatUiFallbackText(spec: ChatUiSpec, language = "fr"): string {
  const fr = language === "fr";
  const colon = fr ? " : " : ": ";
  const num = new Intl.NumberFormat(fr ? "fr-FR" : "en-US", { maximumFractionDigits: 2 });
  const lines: string[] = [];
  const el = (id: string): ChatUiElement | undefined => (Object.hasOwn(spec.elements, id) ? spec.elements[id] : undefined);
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  const hrefOf = (e: ChatUiElement): string | null => (e.on?.press.action === "url" ? e.on.press.params.href : null);

  const inline = (id: string): string | null => {
    const e = el(id);
    if (!e) return null;
    const p = e.props;
    switch (e.type) {
      case "Stat":
        return `${str(p.value)} ${str(p.label)}`;
      case "PersonChip":
        return `${str(p.name)} (${str(p.why)})`;
      case "Weather":
        return weatherLine(p, fr);
      case "ActionChip": {
        const href = hrefOf(e);
        return href ? `${str(p.label)} — ${href}` : null;
      }
      default:
        return null;
    }
  };

  const block = (id: string): void => {
    const e = el(id);
    if (!e) return;
    const p = e.props;
    const kids = e.children.map(el).filter((c): c is ChatUiElement => c !== undefined);
    switch (e.type) {
      case "Stack":
        for (const c of e.children) block(c);
        return;
      case "Row": {
        const parts = e.children.map(inline).filter((t): t is string => Boolean(t));
        if (parts.length) lines.push(parts.join(" · "));
        return;
      }
      case "Headline":
      case "Text":
        lines.push(str(p.text));
        return;
      case "DayTimeline":
        if (p.title) lines.push(str(p.title));
        for (const t of kids) lines.push(`${str(t.props.time)} · ${str(t.props.label)}${t.props.note ? ` (${str(t.props.note)})` : ""}`);
        return;
      case "Highlight":
        lines.push(`${str(p.title)}${p.text ? ` — ${str(p.text)}` : ""}`);
        for (const c of e.children) {
          const chip = inline(c);
          if (chip) lines.push(chip);
        }
        return;
      case "Stat":
      case "PersonChip":
      case "Weather":
      case "ActionChip": {
        const t = inline(id);
        if (t) lines.push(t);
        return;
      }
      case "Progress":
        lines.push(`${str(p.label)}${colon}${p.percent}${fr ? " %" : "%"}`);
        return;
      case "Options":
        kids.forEach((o, i) => {
          const details = [o.props.subtitle, o.props.meta, o.props.badge].map(str).filter(Boolean).join(" · ");
          const href = hrefOf(o);
          lines.push(`${i + 1}. ${str(o.props.title)}${details ? ` — ${details}` : ""}${href ? ` ${href}` : ""}`);
        });
        return;
      case "LinkCard":
        lines.push(`${str(p.title)} — ${hrefOf(e) ?? str(p.url)}`);
        return;
      case "Sparkline": {
        const values = Array.isArray(p.values) ? p.values.filter((v): v is number => typeof v === "number") : [];
        if (values.length < 2) return;
        const unit = p.unit ? ` ${str(p.unit)}` : "";
        lines.push(`${str(p.label)}${colon}${num.format(values[0]!)} → ${num.format(values[values.length - 1]!)}${unit}`);
        return;
      }
      case "Checklist":
        if (p.title) lines.push(str(p.title));
        for (const c of kids) lines.push(`${c.props.done === true ? "☑" : "☐"} ${str(c.props.label)}`);
        return;
      case "Table": {
        if (p.title) lines.push(str(p.title));
        const columns = Array.isArray(p.columns) ? p.columns.map(str) : [];
        const rows = Array.isArray(p.rows) ? p.rows : [];
        rows.forEach((row, n) => {
          const cells = Array.isArray(row) ? row.map(str) : [];
          const rest = cells
            .slice(1)
            .map((c, i) => (c ? `${columns[i + 1] ? `${columns[i + 1]}${colon}` : ""}${c}` : ""))
            .filter(Boolean)
            .join(" · ");
          lines.push(`${p.highlight === n + 1 ? "★ " : ""}${cells[0] ?? ""}${rest ? ` — ${rest}` : ""}`);
        });
        return;
      }
      case "Products":
        kids.forEach((o, i) => {
          const details = [o.props.price, o.props.subtitle, o.props.badge].map(str).filter(Boolean).join(" · ");
          const href = hrefOf(o);
          lines.push(`${i + 1}. ${str(o.props.title)}${details ? ` — ${details}` : ""}${href ? ` ${href}` : ""}`);
        });
        return;
      case "Journey":
        lines.push(journeyLine(p));
        return;
      default:
        return;
    }
  };

  block(spec.root);
  let text = "";
  for (const line of lines.map((l) => l.trim()).filter(Boolean)) {
    if (text.length + line.length + 1 > FALLBACK_MAX) return `${text}\n…`.trim();
    text = text ? `${text}\n${line}` : line;
  }
  return text;
}

// ───────────────────────────── What to answer, under an image ─────────────────────────────

/** A choice the owner can answer (an Option, Product or Journey whose press sends a message), numbered in tree order. */
export interface ChatUiChoice {
  id: string;
  /** Its number, from 1. */
  n: number;
  label: string;
}

/** The elements of the spec in tree order (each once). */
function treeOrder(spec: ChatUiSpec): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (id: string): void => {
    if (seen.has(id) || !Object.hasOwn(spec.elements, id)) return;
    seen.add(id);
    out.push(id);
    for (const c of spec.elements[id]!.children) walk(c);
  };
  walk(spec.root);
  return out;
}

/**
 * The numbered choices of an interface shown as an image (a picture cannot be tapped): the image shows the number on
 * each one, the text under it lists them, so the owner answers « 2 » or its name.
 */
export function chatUiChoices(spec: ChatUiSpec): ChatUiChoice[] {
  const out: ChatUiChoice[] = [];
  for (const id of treeOrder(spec)) {
    const e = spec.elements[id]!;
    if (e.on?.press.action !== "ask") continue;
    const str = (v: unknown): string => (typeof v === "string" ? v : "");
    let label: string | null = null;
    if (e.type === "Option" || e.type === "Product") label = str(e.props.title);
    else if (e.type === "Journey") {
      // « 🚆 7 h 04 Paris → 9 h 01 Lyon · 39 € »: enough to tell the choices apart (the image has the rest).
      const p = e.props;
      const icon = JOURNEY_ICONS[str(p.mode) as JourneyMode] ?? "";
      const from = [str(p.depart), str(p.from)].filter(Boolean).join(" ");
      const to = [str(p.arrive), str(p.to)].filter(Boolean).join(" ");
      label = `${icon ? `${icon} ` : ""}${from} → ${to}${p.price ? ` · ${str(p.price)}` : ""}`;
    }
    if (label) out.push({ id, n: out.length + 1, label });
  }
  return out;
}

/**
 * The short text sent under the image of an interface (iMessage, SMS, Telegram): the numbered choices, the buttons
 * that answer a message (« Réponds « Oui » ou « Plus tard » »), and the useful links (« title — url », 4 at most). Never
 * the content itself: the image carries it. "" when there is nothing to answer nor to open.
 */
export function chatUiReplyText(spec: ChatUiSpec, language = "fr"): string {
  const fr = language === "fr";
  const lines: string[] = [];
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  for (const c of chatUiChoices(spec)) lines.push(`${c.n}. ${c.label}`);
  const chips: string[] = [];
  const links = new Map<string, string>();
  for (const id of treeOrder(spec)) {
    const e = spec.elements[id]!;
    const press = e.on?.press;
    if (!press) continue;
    if (press.action === "ask" && e.type === "ActionChip") chips.push(`« ${press.params.message} »`);
    if (press.action === "url" && !links.has(press.params.href)) {
      const title = str(e.props.title) || str(e.props.label);
      links.set(press.params.href, title);
    }
  }
  if (chips.length) {
    const list = chips.length === 1 ? chips[0]! : `${chips.slice(0, -1).join(", ")} ${fr ? "ou" : "or"} ${chips[chips.length - 1]}`;
    lines.push(`${lines.length ? (fr ? "Ou réponds " : "Or reply ") : fr ? "Réponds " : "Reply "}${list}`);
  }
  for (const [href, title] of [...links].slice(0, 4)) lines.push(title ? `${title} — ${href}` : href);
  return lines.join("\n");
}

// ───────────────────────────── The model's part of the prompt ─────────────────────────────

/**
 * The catalog's own part of `catalog.prompt()`: its components (with their props) and its actions. json-render's
 * generic sections (JSONL patches, state, repeat, visibility, watchers, built-in actions) do not apply to a spec
 * passed whole to show_ui, so they are left out.
 */
export function chatUiCatalogPrompt(): string {
  const full = chatUiCatalog.prompt();
  const start = full.search(/^AVAILABLE COMPONENTS \(/m);
  const end = full.indexOf("\nEVENTS (", start);
  if (start === -1 || end === -1) throw new Error("chat-ui: unexpected catalog prompt layout");
  return full
    .slice(start, end)
    .split("\n")
    .filter((line) => !line.includes("[built-in]"))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
